import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  BugReport as TestingIcon
} from '@mui/icons-material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import AddSectionModal from '../atoms/AddSectionModal';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import LoadingBox from '../atoms/LoadingBox';
import TopBarButtons from '../atoms/TopBarButtons';
import InlineImproveInput from '../common/InlineImproveInput';
import MarkdownRenderer from '../common/MarkdownRenderer';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';

// Import hooks
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useExplain from '../../hooks/useExplain';
import useImprove from '../../hooks/useImprove';
import useFullScreen from '../../hooks/useFullScreen';
import useUnitTestingVersions from '../../hooks/useUnitTestingVersions';
import conversationService from '../../services/conversationService';
import useParsedSections from '../../hooks/useParsedSections';

/**
 * A split-view layout component that shows conversation on the left
 * and unit testing cases in a structured format on the right with full CRUD capabilities
 */
const SplitViewUnitTesting = ({
  conversation,
  children,
  isStreaming,
  streamingContent,
  projectId,
  onConversationUpdate
}) => {
  const { showNotification } = useNotification();
  const testingPanelRef = useRef(null);
  const isLocalUpdateRef = useRef(false);

  // Version management - pass isLocalUpdateRef
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useUnitTestingVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for managing expanded/visible sections
  const [expandedSections, setExpandedSections] = useState({});

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);
  const [visibleSections, setVisibleSections] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);

  // Track previous version index
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Determine if this is a unit testing conversation
  const isUnitTestingConversation = conversation?.taskId === 'unit-testing';
  const showTestingPanel = parsedSections.length > 0;

  // Full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // CRUD operations
  const {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
  } = useSectionCRUD(
    conversation,
    parsedSections,
    setParsedSections,
    setExpandedSections,
    contentRef,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef,
    onConversationUpdate,
    markLocalEdit
  );

  // Content actions (export, copy, refresh)
  const {
    isExporting,
    handleExport,
    getExportContent,
    handleCopy,
    handleRefreshDiagrams
  } = useContentActions(contentRef, 'Unit Testing', projectId, conversation?._id);

  // Explain functionality
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Improve functionality
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Validation
  const initialValidationState = parsedSections.reduce((acc, section) => {
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  const {
    validatedItems,
    handleValidateItem,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'unit-testing',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'unit-testing',
  });

  // Wrapper for version-specific validation
  const handleValidateSection = async (sectionId) => {
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateItem(versionSpecificId);
  };

  // Handle section toggle
  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Pagination handlers
  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, parsedSections.length));
  };

  const handleShowAll = () => {
    setVisibleSections(parsedSections.length);
  };

  // Copy section handler
  const handleCopySection = (section) => {
    try {
      let sectionText = `${section.title}\n${'='.repeat(section.title.length)}\n\n`;
      sectionText += section.content || '';

      navigator.clipboard.writeText(sectionText);
      showNotification({
        message: `"${section.title}" copied to clipboard`,
        type: 'success'
      });
    } catch (error) {
      showNotification({
        message: 'Failed to copy section',
        type: 'error'
      });
    }
  };

  // Handle improve update
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      const contentToUse = improveSection.mergedContent || improvedContent;

      const updatedSections = parsedSections.map(s => {
        if (s.id === improveSection.id) {
          return {
            ...s,
            content: contentToUse,
            rawContent: contentToUse
          };
        }
        return s;
      });

      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Reconstruct content
      const updatedContent = reconstructContent(updatedSections);
      contentRef.current = updatedContent;

      // Update version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          sections: updatedSections,
          parsedContent: updatedSections
        });
      }

      // Save to backend
      await saveToBackend(updatedContent);

      closeImprovementModal();
      showNotification({
        message: 'Section improved successfully',
        type: 'success'
      });
    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Reconstruct full markdown content from sections
  const reconstructContent = (sections) => {
    let content = '';
    let lastCategory = null;

    sections.forEach((section, idx) => {
      if (idx > 0) content += '\n\n';

      // Add category header (H2) if new category
      if (section.isFirstInCategory && section.category && section.category !== lastCategory) {
        content += `## ${section.category}\n\n`;
        lastCategory = section.category;
      }

      // Add section header (H3) and content
      content += `### ${section.title}\n\n${section.content}`;
    });

    return content.trim();
  };

  // Save to backend
  const saveToBackend = async (updatedContent) => {
    const updatedMessages = [...conversation.messages];

    // Find and update the message for current version
    let messageUpdated = false;
    if (currentVersion?.id) {
      for (let i = 0; i < updatedMessages.length; i++) {
        if (updatedMessages[i]._id === currentVersion.id || updatedMessages[i].id === currentVersion.id) {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent
          };
          messageUpdated = true;
          break;
        }
      }
    }

    // Fallback: update last assistant message
    if (!messageUpdated) {
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent
          };
          break;
        }
      }
    }

    const conversationData = {
      messages: updatedMessages.map(msg => ({
        role: msg.role,
        content: msg.content,
        attachments: msg.attachments || [],
        createdAt: msg.createdAt || new Date().toISOString()
      }))
    };

    await conversationService.saveConversation(conversation._id, conversationData);
  };

  // Handle add new section
  const handleAddNewSection = async (newSectionData) => {
    try {
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new sections',
          type: 'warning'
        });
        setShowAddModal(false);
        return;
      }

      const newSection = {
        id: `custom-${Date.now()}`,
        title: newSectionData.title,
        content: newSectionData.content,
        rawContent: newSectionData.content,
        category: 'Custom',
        isFirstInCategory: false,
        type: 'custom'
      };

      const updatedSections = [...parsedSections, newSection];

      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Expand new section
      setExpandedSections(prev => ({ ...prev, [newSection.id]: true }));

      // Ensure visible
      if (visibleSections < updatedSections.length) {
        setVisibleSections(updatedSections.length);
      }

      // Reconstruct and save
      const updatedContent = reconstructContent(updatedSections);
      contentRef.current = updatedContent;

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          sections: updatedSections,
          parsedContent: updatedSections
        });
      }

      await saveToBackend(updatedContent);

      showNotification({
        message: 'Section added successfully',
        type: 'success'
      });

      setShowAddModal(false);

      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Scroll to new section
      setTimeout(() => {
        if (testingPanelRef.current) {
          testingPanelRef.current.scrollTop = testingPanelRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      showNotification({
        message: 'Failed to add new section',
        type: 'error'
      });
    }
  };

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && testingPanelRef.current && isUnitTestingConversation) {
      setTimeout(() => {
        if (testingPanelRef.current) {
          testingPanelRef.current.scrollTop = testingPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isUnitTestingConversation]);

  // Don't show split view if not unit testing conversation or no content
  if (!isUnitTestingConversation || (!showTestingPanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>
      {/* Main content with split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Testing panel */}
        <Box
          ref={testingPanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: '8px',
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {/* Header */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SectionHeader label='Unit Testing' />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExport}
                  isExporting={isExporting}
                  onCopy={handleCopy}
                  onRefresh={handleRefreshDiagrams}
                  disabled={!contentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add sections" : "Add test section"}
                  exportTooltip="Export as markdown"
                  copyTooltip="Copy to clipboard"
                  refreshTooltip="Refresh diagrams"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'unit-testing'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedItems}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Content */}
            {isStreaming ? (
              <LoadingBox message='Generating unit test cases...' spinnerSize={40} />
            ) : showTestingPanel && parsedSections.length > 0 ? (
              <Box sx={{
                p: 3,
                pt: 2,
                flex: 1,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
              }}>
                {/* Render sections grouped by category */}
                {(() => {
                  const groups = groupedSections;
                  let globalIndex = 0;
                  return groups.slice(0, visibleSections).map((group, groupIndex) => (
                    <Box key={group.categoryId} sx={{ mb: 3 }}>
                      {group.category && (
                        <Box sx={{ mt: groupIndex === 0 ? 0 : 2, mb: 1.5 }}>
                          <SectionHeader label={group.category} />
                        </Box>
                      )}
                      {group.categorySection && (
                        <Box sx={{ pl: 2, mb: 2 }}>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                            <MarkdownRenderer content={group.categorySection.content || ''} />
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ pl: group.category ? 2 : 0 }}>
                        {group.sections.map((section) => {
                          const idx = globalIndex++;
                          const sectionId = section.id;
                          const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                          const isValidated = validatedItems[versionSpecificId] || false;
                          return (
<Accordion
                        ref={(el) => { registerSectionRef(section.id, el); if (shouldHighlight(section.id)) highlightRef.current = el; }}
                        title={section.title}
                        defaultExpanded={false}
                        expanded={expandedSections[section.id]}
                        onChange={(_, expanded) => handleSectionToggle(section.id)}
                        titleContent={editingSection === section.id ? (
                          <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                            <TextField
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.target.removeAttribute('title')}
                              size="small"
                              fullWidth
                              placeholder="Enter unit test title"
                              variant="outlined"
                              InputProps={{ title: '' }}
                              inputProps={{ title: '', autoComplete: 'off' }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  backgroundColor: '#fff',
                                  '& fieldset': { borderWidth: '1px' },
                                  '&:hover fieldset': { borderWidth: '1px' },
                                  '&.Mui-focused fieldset': { borderWidth: '1px' },
                                },
                                '& .MuiOutlinedInput-input': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  padding: '8px 12px',
                                },
                              }}
                            />
                          </Box>
                        ) : null}
                        rightIcon={
                          <Box
                            onClick={async (e) => {
                              e.stopPropagation();
                              const isCurrentlyValidated = validatedItems[versionSpecificId];

                              try {
                                await handleValidateSection(section.id);

                                showNotification({
                                  message: isCurrentlyValidated
                                    ? `Validation removed for "${section.title}"`
                                    : `"${section.title}" validated successfully`,
                                  type: isCurrentlyValidated ? 'error' : 'success'
                                });
                              } catch (error) {
                                showNotification({
                                  message: 'Failed to update validation',
                                  type: 'error'
                                });
                              }
                            }}
                            sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                          >
                            {isValidated ? (
                              <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                            ) : (
                              <UnvalidatedIcon size={25} />
                            )}
                          </Box>
                        }
                      >
                        {/* Section content */}
                        <ScrollableBox
                          minHeight="160px"
                          sx={{
                            textAlign: 'justify',
                            cursor: editingSection === section.id ? 'default' : 'pointer',
                          }}
                        >
                          {editingSection === section.id ? (
                            <EditableTextField
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                            />
                          ) : (
                            <Box ref={getSectionContentRef(section.id)}>
                              <MarkdownRenderer content={section.content || ''} />
                              <TextSelectionPopup
                                containerRef={getSectionContentRef(section.id)}
                                sectionId={section.id}
                                onSubmitComment={addComment}
                                disabled={editingSection === section.id}
                              />

                              {/* Inline improve input */}
                              {showImproveInput === section.id && (
                                <InlineImproveInput
                                  improvePrompt={improvePrompt}
                                  onPromptChange={setImprovePrompt}
                                  onSend={() => handleSendImprove(section)}
                                  onCancel={handleCancelImprove}
                                  promptText="How would you like to improve this section?"
                                  placeholder="Describe the improvements..."
                                />
                              )}
                            </Box>
                          )}
                        </ScrollableBox>

                        {/* Action buttons */}
                        <Box sx={{
                          position: 'absolute',
                          right: 10,
                          top: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}>
                          <ActionButtons
                            item={section}
                            itemIndex={idx}
                            featureId={section.id}
                            editingFeature={editingSection}
                            isSaving={isSaving}
                            onEdit={() => handleStartEdit(section.id, section.content, section.title || '')}
                            onSave={() => handleSaveEdit(section.id)}
                            onCancel={handleCancelEdit}
                            onImprove={() => handleImproveClick(section)}
                            onExplain={() => requestExplanation(section)}
                            onCopy={() => handleCopySection(section)}
                            onDelete={() => handleDeleteSection(section)}
                            vertical={true}
                          onComment={() => openCommentPanel(section.id)}
                          commentCount={commentCounts[section.id]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                          />
                        </Box>
                      </Accordion>
                          );
                        })}
                      </Box>
                    </Box>
                  ));
                })()}

                {/* Load more button */}
                {(() => {
                  const groups = groupedSections;
                  return groups.length > visibleSections && (
                    <LoadMoreButton
                      remaining={groups.length - visibleSections}
                      onLoadMore={handleLoadMore}
                      onShowAll={handleShowAll}
                      label="Load more"
                    />
                  );
                })()}
              </Box>
            ) : (
              <EmptyStateBox message="No unit testing content found yet." />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Modals */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteSectionConfirmed}
      />

      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      <AddSectionModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddNewSection}
        sectionType="test-section"
        existingSections={parsedSections}
      />

      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewUnitTesting;
